import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Trash2, MapPin, ExternalLink, Upload, Mic, Save, AlertTriangle, AlertCircle, User, Calendar, ChevronLeft, ChevronRight, Pencil, Link as LinkIcon } from 'lucide-react';
import { format } from 'date-fns';
import { findNextAvailableSlot } from '../../utils/timeUtils';
import { UserRole } from '../../types';
import { compressImages } from '../../utils/imageUtils';
import { UNIQUE_COUNTRIES } from '../../utils/countries';

export default function ModalDetalhesAssistencia({ 
  assistencia, 
  onClose, 
  onUpdate, 
  onDelete,
  setTasks,
  company,
  currentUser,
  tasks = [],
  team = []
}) {
  const [activeTab, setActiveTab] = useState('DADOS_TECNICOS');
  const [status, setStatus] = useState(assistencia?.status || 'ABERTA');
  const [isPaid, setIsPaid] = useState(assistencia?.isPaid === true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null); // Para lightbox de fotos
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0); // Índice da foto selecionada
  const [selectedPhotoArray, setSelectedPhotoArray] = useState([]); // Array de fotos para navegação
  const [showPhotoDeleteConfirm, setShowPhotoDeleteConfirm] = useState(false); // Modal de confirmação para remover foto
  const [photoToDelete, setPhotoToDelete] = useState({ index: null, type: null }); // Foto a ser removida
  
  // Render-safe money formatting: Firestore can sometimes return numbers as strings.
  const formatMoney = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value.toFixed(2);
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.').trim();
      const n = Number(normalized);
      if (Number.isFinite(n)) return n.toFixed(2);
    }
    if (value === null || value === undefined) return '0.00';
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
  };
  
  // Estados para edição
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    client: '',
    phone: '',
    locality: '',
    locationLink: '',
    brand: '',
    reference: '',
    faultDate: '',
    description: '',
    isWarranty: false
  });
  const [editCountryCode, setEditCountryCode] = useState('351');
  const [editPhotos, setEditPhotos] = useState([]);
  
  // Estados para TRABALHO REALIZADO
  const [resolucao, setResolucao] = useState(assistencia?.resolucao || '');
  const [notasTecnico, setNotasTecnico] = useState(assistencia?.notasTecnico || '');
  const [fotosTrabalho, setFotosTrabalho] = useState(assistencia?.additionalPhotos || []);
  
  const fileInputRef = useRef(null);
  const fileInputTrabalhoRef = useRef(null);
  const editFileInputRef = useRef(null);

  // Verificar se já existe tarefa para esta assistência
  const existingTask = useMemo(() => {
    return tasks.find(t => 
      t.relatedType === 'TECH' && 
      t.relatedId === assistencia?.id &&
      !t.isCompleted
    );
  }, [tasks, assistencia?.id]);

  // Filtrar utilizadores que podem receber tarefas (excluir ADMIN)
  const assignableTeam = useMemo(() => {
    return team.filter(u => u?.role !== UserRole.ADMIN);
  }, [team]);

  // Atualizar estados quando assistencia mudar
  useEffect(() => {
    if (assistencia) {
      setStatus(assistencia.status || 'ABERTA');
      setIsPaid(assistencia.isPaid === true);
      setResolucao(assistencia.resolucao || '');
      setNotasTecnico(assistencia.notasTecnico || '');
      // Garantir que additionalPhotos seja sempre um array válido
      setFotosTrabalho(Array.isArray(assistencia.additionalPhotos) ? assistencia.additionalPhotos : []);
    }
  }, [assistencia]);

  // Inicializar formulário de edição
  const startEditing = () => {
    if (!assistencia) return;
    
    // Extrair código do país do telefone se existir
    let phoneNumber = assistencia.phone || '';
    let countryCode = '351'; // Padrão
    if (phoneNumber.startsWith('+')) {
      // Tentar extrair código do país
      for (const country of UNIQUE_COUNTRIES) {
        if (phoneNumber.startsWith(`+${country.code}`)) {
          countryCode = country.code;
          phoneNumber = phoneNumber.substring(country.code.length + 1);
          break;
        }
      }
    }
    
    setEditForm({
      client: assistencia.client || '',
      phone: phoneNumber,
      locality: assistencia.locality || '',
      locationLink: assistencia.locationLink || '',
      brand: assistencia.brand || '',
      reference: assistencia.reference || '',
      faultDate: assistencia.faultDate || assistencia.date || new Date().toISOString().slice(0, 10),
      description: assistencia.description || '',
      isWarranty: assistencia.isWarranty === true
    });
    setEditCountryCode(countryCode);
    setEditPhotos(Array.isArray(assistencia.photos) ? assistencia.photos : []);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({
      client: '',
      phone: '',
      locality: '',
      locationLink: '',
      brand: '',
      reference: '',
      faultDate: '',
      description: '',
      isWarranty: false
    });
    setEditCountryCode('351');
    setEditPhotos([]);
  };

  const saveEditing = () => {
    if (!assistencia) return;
    
    const updated = {
      ...assistencia,
      client: editForm.client,
      phone: editForm.phone.startsWith('+') || editForm.phone.startsWith('00') 
        ? editForm.phone 
        : `+${editCountryCode}${editForm.phone}`,
      locality: editForm.locality,
      locationLink: editForm.locationLink,
      brand: editForm.brand,
      reference: editForm.reference,
      faultDate: editForm.faultDate,
      description: editForm.description,
      isWarranty: editForm.isWarranty,
      photos: editPhotos
    };
    
    // Update the assistance
    onUpdate(updated);
    
    // Exit edit mode after saving
    setIsEditing(false);
    
    // Reset edit form
    setEditForm({
      client: '',
      phone: '',
      locality: '',
      locationLink: '',
      brand: '',
      reference: '',
      faultDate: '',
      description: '',
      isWarranty: false
    });
    setEditCountryCode('351');
    setEditPhotos([]);
  };

  const handleEditPhotoUpload = async (files) => {
    try {
      const compressedPhotos = await compressImages(files, 1920, 1920, 0.8, 400);
      setEditPhotos(prev => [...prev, ...compressedPhotos].slice(0, 20));
    } catch (error) {
      console.error('Erro ao comprimir fotos:', error);
      const arr = Array.from(files || []);
      const readers = arr.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(file);
      }));
      const base64s = await Promise.all(readers);
      setEditPhotos(prev => [...prev, ...base64s].slice(0, 20));
    }
  };

  if (!assistencia) return null;

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    onUpdate({ ...assistencia, status: newStatus, isPaid });
  };

  const handlePaidChange = (newPaid) => {
    setIsPaid(newPaid);
    onUpdate({ ...assistencia, status, isPaid: newPaid });
  };

  const handlePhotoUpload = async (files, isTrabalho = false) => {
    try {
      // Comprimir imagens antes de converter para base64
      // maxWidth: 1920, maxHeight: 1920, quality: 0.8, maxSizeKB: 400
      const compressedPhotos = await compressImages(files, 1920, 1920, 0.8, 400);
      
      if (isTrabalho) {
        const novasFotos = [...(Array.isArray(fotosTrabalho) ? fotosTrabalho : []), ...compressedPhotos].slice(0, 20);
        setFotosTrabalho(novasFotos);
      } else {
        // Garantir que photos seja sempre um array válido
        const currentPhotos = Array.isArray(assistencia.photos) ? assistencia.photos : [];
        const novasFotos = [...currentPhotos, ...compressedPhotos].slice(0, 20);
        onUpdate({ ...assistencia, photos: novasFotos });
      }
    } catch (error) {
      console.error('Erro ao comprimir fotos:', error);
      // Fallback: tentar sem compressão
      const arr = Array.from(files || []);
      const readers = arr.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(file);
      }));
      const base64s = await Promise.all(readers);
      
      if (isTrabalho) {
        const novasFotos = [...(Array.isArray(fotosTrabalho) ? fotosTrabalho : []), ...base64s].slice(0, 20);
        setFotosTrabalho(novasFotos);
      } else {
        const currentPhotos = Array.isArray(assistencia.photos) ? assistencia.photos : [];
        const novasFotos = [...currentPhotos, ...base64s].slice(0, 20);
        onUpdate({ ...assistencia, photos: novasFotos });
      }
    }
  };

  const handleGuardarProgresso = () => {
    onUpdate({
      ...assistencia,
      resolucao,
      notasTecnico,
      additionalPhotos: fotosTrabalho,
      status,
      isPaid
    });
    // O feedback de sucesso é mostrado pelo onUpdate no App.js
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete(assistencia.id);
    onClose();
    setShowDeleteConfirm(false);
  };

  const handleVerNoMapa = () => {
    if (assistencia.locationLink) {
      window.open(assistencia.locationLink, '_blank');
    }
  };

  const handleCreateWarrantyTask = (selectedUserId = null) => {
    if (!setTasks || !company || !currentUser) return;
    
    if (existingTask) {
      alert('Já existe uma tarefa ativa criada para esta assistência técnica sob garantia.');
      return;
    }
    
    // Se não foi selecionado um utilizador, mostrar seletor
    if (!selectedUserId && assignableTeam.length > 1) {
      setShowUserSelector(true);
      return;
    }
    
    // Usar utilizador selecionado ou currentUser como fallback
    const targetUser = selectedUserId 
      ? assignableTeam.find(u => u.id === selectedUserId) || currentUser
      : currentUser;
    
    if (!targetUser) return;
    
    // Obter hora atual
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    const taskDate = assistencia.faultDate || assistencia.date || format(now, 'yyyy-MM-dd');
    
    // Encontrar próximo slot disponível
    const tasksForUserOnDate = tasks.filter(t => 
      t.assignedTo === targetUser.id && 
      t.date === taskDate &&
      t.startTime && 
      t.endTime
    );
    
    const { startTime, endTime } = findNextAvailableSlot(
      currentTime,
      tasksForUserOnDate,
      targetUser.id,
      taskDate
    );
    
    setTasks(prev => [...prev, {
      id: `task-warranty-${Date.now()}-${assistencia.id}`,
      companyId: company.id,
      name: 'Dizer Tratamento Garantia',
      date: taskDate,
      startTime: startTime,
      endTime: endTime,
      assignedTo: targetUser.id,
      isCompleted: false,
      status: 'A Fazer',
      relatedType: 'TECH',
      relatedId: assistencia.id,
      notes: `Cliente: ${assistencia.client || ''}\n${assistencia.locality ? `Localidade: ${assistencia.locality}\n` : ''}${assistencia.phone ? `Tel: ${assistencia.phone}\n` : ''}${assistencia.locationLink ? `Link: ${assistencia.locationLink}` : ''}`.trim()
    }]);
    
    // Fechar seletor se estava aberto
    setShowUserSelector(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    } catch {
      return dateStr;
    }
  };

  const getReferenceNumber = () => {
    // Gerar número de referência baseado no ID ou usar um campo específico
    if (assistencia.referenceNumber) return assistencia.referenceNumber;
    // Usar referência do equipamento se existir
    if (assistencia.reference) return `#${assistencia.reference}`;
    // Usar últimos dígitos do timestamp ou ID
    if (assistencia.id) {
      try {
        // Tentar extrair número do ID ou usar hash simples
        const num = assistencia.id.length > 4 
          ? parseInt(assistencia.id.slice(-4), 16) % 10000
          : Date.now() % 10000;
        return `#${num.toString().padStart(4, '0')}`;
      } catch {
        // Fallback seguro
        const fallback = assistencia.id.slice(-4).replace(/\D/g, '') || '0000';
        return `#${fallback.padStart(4, '0')}`;
      }
    }
    return '#0000';
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[140] flex items-center justify-center p-6 overflow-hidden">
      <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl my-auto overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header Verde Escuro */}
        <div className="bg-gradient-to-r from-[#1b5e20] to-[#2e7d32] text-white p-6">
          <div className="flex justify-between items-start">
            <div className="flex-1 pr-4">
              <h1 className="text-2xl font-black mb-3">{assistencia.client || '—'}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-lg">
                  <MapPin size={16} />
                  <span className="font-bold">{assistencia.locality || '—'}</span>
                </div>
                <div className="bg-white/10 px-3 py-1.5 rounded-lg">
                  <span className="font-bold">Ref: {getReferenceNumber()}</span>
                </div>
                {assistencia.phone && (
                  <div className="bg-white/10 px-3 py-1.5 rounded-lg">
                    <span className="font-bold">{assistencia.phone}</span>
                  </div>
                )}
                {assistencia.locationLink && (
                  <button
                    onClick={handleVerNoMapa}
                    className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors font-bold"
                  >
                    Ver no Mapa
                    <ExternalLink size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <button
                  onClick={startEditing}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                  title="Editar Assistência"
                >
                  <Pencil size={18} />
                </button>
              )}
              <button
                onClick={handleDelete}
                className="p-2 bg-red-600/90 hover:bg-red-700 rounded-xl transition-colors shadow-lg"
                title="Eliminar"
              >
                <Trash2 size={18} />
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                title="Fechar"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          <button
            onClick={() => setActiveTab('DADOS_TECNICOS')}
            className={`flex-1 py-3 px-4 font-bold text-xs transition-all ${
              activeTab === 'DADOS_TECNICOS'
                ? 'bg-white text-[#1b5e20] border-b-4 border-[#1b5e20] shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            Dados Técnicos
          </button>
          <button
            onClick={() => setActiveTab('TRABALHO_REALIZADO')}
            className={`flex-1 py-3 px-4 font-bold text-xs transition-all ${
              activeTab === 'TRABALHO_REALIZADO'
                ? 'bg-white text-[#1b5e20] border-b-4 border-[#1b5e20] shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            Trabalho Realizado
          </button>
          <button
            onClick={() => setActiveTab('ORCAMENTOS')}
            className={`flex-1 py-3 px-4 font-bold text-xs transition-all ${
              activeTab === 'ORCAMENTOS'
                ? 'bg-white text-[#1b5e20] border-b-4 border-[#1b5e20] shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            Orçamentos ({assistencia.budgets?.length || 0})
          </button>
        </div>

        {/* Status Buttons */}
        <div className="p-4 bg-gradient-to-r from-slate-50 to-green-50/30 border-b border-slate-200 space-y-3">
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => handleStatusChange('ABERTA')}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm ${
                status === 'ABERTA'
                  ? 'bg-red-500 text-white shadow-md scale-105'
                  : 'bg-white border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'
              }`}
            >
              Aberta
            </button>
            <button
              onClick={() => handleStatusChange('EM_TRATAMENTO')}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm ${
                status === 'EM_TRATAMENTO'
                  ? 'bg-orange-500 text-white shadow-md scale-105'
                  : 'bg-white border-2 border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300'
              }`}
            >
              Em Tratamento
            </button>
            <button
              onClick={() => handleStatusChange('CONCLUIDA')}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm ${
                status === 'CONCLUIDA'
                  ? 'bg-green-600 text-white shadow-md scale-105'
                  : 'bg-white border-2 border-green-200 text-green-600 hover:bg-green-50 hover:border-green-300'
              }`}
            >
              Concluída
            </button>
          </div>
          {/* Paid/Unpaid Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => handlePaidChange(false)}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm ${
                !isPaid
                  ? 'bg-slate-600 text-white shadow-md scale-105'
                  : 'bg-white border-2 border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
              }`}
            >
              Não Pago
            </button>
            <button
              onClick={() => handlePaidChange(true)}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm ${
                isPaid
                  ? 'bg-green-600 text-white shadow-md scale-105'
                  : 'bg-white border-2 border-green-200 text-green-600 hover:bg-green-50 hover:border-green-300'
              }`}
            >
              Pago
            </button>
          </div>
          {/* Product under warranty button - only show if warranty and no existing task */}
          {assistencia.isWarranty && !existingTask && (
            <div className="flex gap-3">
              <button
                onClick={() => handleCreateWarrantyTask()}
                className="px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm bg-blue-600 text-white hover:bg-blue-700 border-2 border-blue-600 flex items-center gap-2"
              >
                <Calendar size={14} />
                Marcar na Agenda
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 bg-slate-50 flex-1 overflow-y-auto">
          {activeTab === 'DADOS_TECNICOS' && (
            isEditing ? (
              <div className="space-y-4">
                {/* Editable Form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Cliente *</label>
                    <input
                      required
                      value={editForm.client}
                      onChange={(e) => setEditForm(prev => ({ ...prev, client: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                      placeholder="Nome do Cliente"
                    />
                    <Mic size={16} className="absolute right-4 top-12 text-green-700/40" />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Telefone *</label>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <select
                          value={editCountryCode}
                          onChange={(e) => setEditCountryCode(e.target.value)}
                          className="w-full p-4 bg-green-50 rounded-2xl font-black text-xs outline-none border-2 border-transparent focus:border-green-600"
                        >
                          {UNIQUE_COUNTRIES.map(country => (
                            <option key={country.code} value={country.code}>
                              {country.flag} +{country.code}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-8 relative">
                        <input
                          required
                          value={editForm.phone}
                          onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                          placeholder={editCountryCode === '351' ? 'Ex: 912 345 678' : 'Ex: 123456789'}
                        />
                        <Mic size={16} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-green-700/40" />
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Localidade *</label>
                    <input
                      required
                      value={editForm.locality}
                      onChange={(e) => setEditForm(prev => ({ ...prev, locality: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                      placeholder="Morada / Localidade"
                    />
                    <Mic size={16} className="absolute right-4 top-12 text-green-700/40" />
                  </div>

                  <div className="relative">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Link Localização (Maps/Waze)</label>
                    <input
                      value={editForm.locationLink}
                      onChange={(e) => setEditForm(prev => ({ ...prev, locationLink: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                      placeholder="URL para localização..."
                    />
                    <LinkIcon size={16} className="absolute right-4 top-12 text-green-700/40" />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Marca</label>
                    <input
                      value={editForm.brand}
                      onChange={(e) => setEditForm(prev => ({ ...prev, brand: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                      placeholder="Ex: Sony, Bosch..."
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2"># Referência</label>
                    <input
                      value={editForm.reference}
                      onChange={(e) => setEditForm(prev => ({ ...prev, reference: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                      placeholder="Modelo/Nº Série"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Data Avaria</label>
                    <input
                      type="date"
                      value={editForm.faultDate}
                      onChange={(e) => setEditForm(prev => ({ ...prev, faultDate: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Produto sob Garantia?</label>
                    <button
                      type="button"
                      onClick={() => setEditForm(prev => ({ ...prev, isWarranty: !prev.isWarranty }))}
                      className={`w-full p-4 rounded-2xl border-2 transition-colors flex items-center gap-4 ${
                        editForm.isWarranty ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-100'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${editForm.isWarranty ? 'bg-green-800 border-green-800' : 'bg-white border-slate-200'}`}>
                        {editForm.isWarranty && <span className="text-white font-black">✓</span>}
                      </div>
                      <span className="font-black text-slate-800">Produto sob Garantia?</span>
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Descrição do Problema *</label>
                  <textarea
                    required
                    rows={4}
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                    placeholder="Detalhes técnicos da avaria..."
                  />
                  <Mic size={16} className="absolute right-4 top-12 text-green-700/40" />
                </div>

                {/* Photos */}
                <div className="bg-green-50/60 border-2 border-dashed border-green-200 rounded-3xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[10px] font-black text-green-800 uppercase tracking-widest">Fotos do estado de entrada</p>
                    <label className="bg-green-800 text-white px-5 py-3 rounded-xl font-black flex items-center gap-2 cursor-pointer hover:bg-green-900 transition-colors">
                      <Upload size={18} /> Upload
                      <input 
                        ref={editFileInputRef}
                        type="file" 
                        accept="image/*" 
                        multiple 
                        className="hidden" 
                        onChange={(e) => handleEditPhotoUpload(e.target.files)} 
                      />
                    </label>
                  </div>

                  {editPhotos.length === 0 ? (
                    <div className="text-center py-10 text-green-300 font-black">
                      SEM EVIDÊNCIAS CAPTURADAS
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {editPhotos.map((p, idx) => (
                        <div key={idx} className="relative group">
                          <img src={p} alt={`foto-${idx}`} className="w-full h-28 object-cover rounded-2xl border border-green-100" />
                          <button
                            type="button"
                            onClick={() => {
                              setEditPhotos(prev => prev.filter((_, i) => i !== idx));
                            }}
                            className="absolute top-2 right-2 bg-white/90 border border-slate-200 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remover"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Save/Cancel Buttons */}
                <div className="flex gap-4 pt-2">
                  <button 
                    type="button"
                    onClick={cancelEditing} 
                    className="flex-1 py-4 rounded-2xl font-black bg-slate-100 text-slate-400 hover:bg-slate-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button"
                    onClick={saveEditing} 
                    className="flex-[2] py-4 rounded-2xl font-black bg-green-800 text-white hover:bg-green-900 transition-colors shadow-xl"
                  >
                    Guardar Alterações
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Informação do Equipamento e Cronologia */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <div className="w-1 h-4 bg-green-600 rounded-full"></div>
                      Informação do Equipamento
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-slate-500 font-semibold text-sm block mb-1">Marca</span>
                        <span className="text-slate-900 font-bold text-base">{assistencia.brand || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 font-semibold text-sm block mb-1">Modelo</span>
                        <span className="text-slate-900 font-bold text-base">{assistencia.reference || '—'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                      Cronologia
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-slate-500 font-semibold text-sm block mb-1">Reportado em</span>
                        <span className="text-green-600 font-bold text-base">{formatDate(assistencia.faultDate)}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 font-semibold text-sm block mb-1">Entrada no Sistema</span>
                        <span className="text-green-600 font-bold text-base">{formatDate(assistencia.createdAt || assistencia.date)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Descrição do Problema */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <div className="w-1 h-4 bg-amber-600 rounded-full"></div>
                    Descrição do Problema
                  </h3>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600 rounded-xl p-4">
                    <p className="text-sm font-semibold text-slate-800 whitespace-pre-wrap leading-relaxed">
                      {assistencia.description || '—'}
                    </p>
                  </div>
                </div>

                {/* Galeria de Estado de Entrada */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                      <div className="w-1 h-4 bg-purple-600 rounded-full"></div>
                      Galeria de Estado de Entrada
                    </h3>
                    <label className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2 cursor-pointer hover:bg-green-700 transition-colors text-xs shadow-md">
                      <Upload size={14} /> Carregar
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e.target.files, false)}
                      />
                    </label>
                  </div>
                  {(!assistencia.photos || !Array.isArray(assistencia.photos) || assistencia.photos.length === 0) ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center bg-slate-50">
                      <div className="text-slate-300 mb-3">
                        <Upload size={40} className="mx-auto" />
                      </div>
                      <p className="text-slate-500 font-semibold text-sm">
                        Sem documentação visual
                      </p>
                      <p className="text-slate-400 text-xs mt-1">Clique em "Carregar" para adicionar fotos</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                      {assistencia.photos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={photo}
                            alt={`Estado entrada ${idx + 1}`}
                            onClick={() => {
                              setSelectedPhoto(photo);
                              setSelectedPhotoIndex(idx);
                              setSelectedPhotoArray(assistencia.photos || []);
                            }}
                            className="w-full h-24 object-cover rounded-xl border-2 border-slate-200 shadow-sm group-hover:shadow-md transition-shadow cursor-pointer"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevenir que abra o lightbox
                              setPhotoToDelete({ index: idx, type: 'entrada' });
                              setShowPhotoDeleteConfirm(true);
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600 z-10"
                            title="Remover"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          {activeTab === 'TRABALHO_REALIZADO' && (
            <div className="space-y-4">
              {/* Relatório de Intervenção */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                    <div className="w-1 h-4 bg-indigo-600 rounded-full"></div>
                    Relatório de Intervenção
                  </h3>
                  <button
                    onClick={handleGuardarProgresso}
                    className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2 hover:bg-green-700 transition-colors text-xs shadow-md"
                  >
                    <Save size={14} /> Guardar
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Resolução */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-bold text-slate-700">
                        Resolução
                      </label>
                      <Mic size={14} className="text-slate-400" />
                    </div>
                    <textarea
                      value={resolucao}
                      onChange={(e) => setResolucao(e.target.value)}
                      placeholder="Descreva o que foi feito..."
                      rows={4}
                      className="w-full p-3 bg-slate-50 rounded-xl font-semibold text-sm outline-none border-2 border-slate-200 focus:border-green-500 focus:bg-white resize-y transition-colors"
                    />
                  </div>

                  {/* Notas do Técnico */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-bold text-slate-700">
                        Notas do Técnico (Interno)
                      </label>
                      <Mic size={14} className="text-slate-400" />
                    </div>
                    <textarea
                      value={notasTecnico}
                      onChange={(e) => setNotasTecnico(e.target.value)}
                      placeholder="Observações internas..."
                      rows={4}
                      className="w-full p-3 bg-slate-50 rounded-xl font-semibold text-sm outline-none border-2 border-slate-200 focus:border-green-500 focus:bg-white resize-y transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Fotos do Trabalho */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                    <div className="w-1 h-4 bg-teal-600 rounded-full"></div>
                    Fotos do Trabalho
                  </h3>
                  <label className="bg-green-100 text-green-700 px-4 py-2 rounded-xl font-bold flex items-center gap-2 cursor-pointer hover:bg-green-200 transition-colors text-xs shadow-sm">
                    <Upload size={14} /> Carregar
                    <input
                      ref={fileInputTrabalhoRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handlePhotoUpload(e.target.files, true)}
                    />
                  </label>
                </div>
                {fotosTrabalho.length === 0 ? (
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center bg-slate-50">
                    <div className="text-slate-300 mb-3">
                      <Upload size={40} className="mx-auto" />
                    </div>
                    <p className="text-slate-500 font-semibold text-sm">
                      Sem documentação visual
                    </p>
                    <p className="text-slate-400 text-xs mt-1">Clique em "Carregar" para adicionar fotos</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {fotosTrabalho.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={photo}
                          alt={`Trabalho ${idx + 1}`}
                          onClick={() => {
                            setSelectedPhoto(photo);
                            setSelectedPhotoIndex(idx);
                            setSelectedPhotoArray(fotosTrabalho || []);
                          }}
                          className="w-full h-24 object-cover rounded-xl border-2 border-slate-200 shadow-sm group-hover:shadow-md transition-shadow cursor-pointer"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevenir que abra o lightbox
                            setPhotoToDelete({ index: idx, type: 'trabalho' });
                            setShowPhotoDeleteConfirm(true);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600 z-10"
                          title="Remover"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'ORCAMENTOS' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <div className="w-1 h-4 bg-cyan-600 rounded-full"></div>
                  Orçamentos
                </h3>
                {(!assistencia.budgets || assistencia.budgets.length === 0) ? (
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center bg-slate-50">
                    <p className="text-slate-500 font-semibold text-sm">
                      Nenhum orçamento associado
                    </p>
                    <p className="text-slate-400 text-xs mt-1">Os orçamentos aparecerão aqui quando forem criados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assistencia.budgets.map((budget, idx) => (
                      <div key={idx} className="p-4 bg-gradient-to-r from-slate-50 to-green-50/30 rounded-xl border-2 border-slate-200 hover:border-green-300 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-slate-900 text-sm">Orçamento #{idx + 1}</p>
                            {budget.date && (
                              <p className="text-slate-500 text-xs mt-1">{formatDate(budget.date)}</p>
                            )}
                          </div>
                          <p className="font-black text-green-600 text-lg">{formatMoney(budget.total)}€</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Selector Modal for Warranty Task */}
      {showUserSelector && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[150] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-black text-slate-900">Marcar na Agenda</h2>
              <button
                onClick={() => setShowUserSelector(false)}
                className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-slate-600 font-bold mb-6 text-sm">
              Selecione o utilizador para quem deseja criar a tarefa de garantia:
            </p>
            
            <div className="space-y-3 mb-6">
              {assignableTeam.length > 0 ? (
                assignableTeam.map(user => (
                  <button
                    key={user.id}
                    onClick={() => handleCreateWarrantyTask(user.id)}
                    className="w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:shadow-lg transition-all text-left flex items-center gap-3"
                  >
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-black">
                      <User size={18} />
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-slate-900">{user.name || 'Sem nome'}</p>
                      {user.email && (
                        <p className="text-xs text-slate-500">{user.email}</p>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-slate-500 font-bold">Nenhum utilizador disponível</p>
                </div>
              )}
            </div>
            
            <button
              onClick={() => setShowUserSelector(false)}
              className="w-full py-3 bg-slate-100 text-slate-600 font-black rounded-xl hover:bg-slate-200 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Delete */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[150] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-600 sm:w-8 sm:h-8" size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-slate-900">Eliminar Assistência Técnica</h2>
                <p className="text-slate-600 font-bold mt-1 text-sm sm:text-base">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            
            <div className="bg-red-50 border-2 border-red-200 rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-1 sm:w-6 sm:h-6" size={20} />
                <div className="flex-1 min-w-0">
                  <p className="font-black text-red-900 mb-2 text-sm sm:text-base">
                    Tem a certeza absoluta que deseja eliminar a assistência técnica de <span className="text-red-700">{assistencia.client || '—'}</span>?
                  </p>
                  {assistencia.brand && assistencia.reference && (
                    <p className="text-red-800 font-bold text-xs sm:text-sm mt-2">
                      Equipamento: {assistencia.brand} {assistencia.reference}
                    </p>
                  )}
                  <p className="text-red-800 font-bold text-xs sm:text-sm mt-2">
                    Esta ação é permanente e não pode ser desfeita. A assistência técnica será removida completamente do sistema.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-4 sm:py-5 bg-slate-100 text-slate-600 font-black rounded-xl sm:rounded-2xl hover:bg-slate-200 transition-all text-sm sm:text-base"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 py-4 sm:py-5 bg-red-600 text-white font-black rounded-xl sm:rounded-2xl hover:bg-red-700 transition-all shadow-lg text-sm sm:text-base"
              >
                Sim, Eliminar Assistência
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para remover foto */}
      {showPhotoDeleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[150] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-black text-slate-900">Remover Foto</h2>
              <button
                onClick={() => {
                  setShowPhotoDeleteConfirm(false);
                  setPhotoToDelete({ index: null, type: null });
                }}
                className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div>
                  <p className="text-slate-900 font-black text-lg">
                    {photoToDelete.type === 'entrada' ? 'Foto de Estado de Entrada' : 'Foto do Trabalho'}
                  </p>
                  <p className="text-slate-600 font-bold text-sm mt-1">
                    Deseja realmente remover esta foto?
                  </p>
                </div>
              </div>
              
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <p className="text-slate-700 font-bold text-sm">
                  {photoToDelete.type === 'entrada' 
                    ? 'Esta foto será removida da galeria de estado de entrada. Esta ação não pode ser desfeita.'
                    : 'Esta foto será removida da documentação do trabalho realizado. Esta ação não pode ser desfeita.'}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowPhotoDeleteConfirm(false);
                  setPhotoToDelete({ index: null, type: null });
                }}
                className="flex-1 py-3 bg-slate-100 text-slate-600 font-black rounded-xl hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (photoToDelete.type === 'entrada') {
                    const novasFotos = assistencia.photos.filter((_, i) => i !== photoToDelete.index);
                    onUpdate({ ...assistencia, photos: novasFotos });
                  } else if (photoToDelete.type === 'trabalho') {
                    const novasFotos = fotosTrabalho.filter((_, i) => i !== photoToDelete.index);
                    setFotosTrabalho(novasFotos);
                  }
                  setShowPhotoDeleteConfirm(false);
                  setPhotoToDelete({ index: null, type: null });
                }}
                className="flex-1 py-3 bg-red-600 text-white font-black rounded-xl hover:bg-red-700 transition-colors shadow-lg"
              >
                Sim, Remover Foto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox para visualizar fotos em tamanho maior */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-md z-[160] flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Botão fechar */}
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 z-10 transition-colors"
              title="Fechar"
            >
              <X size={24} />
            </button>

            {/* Navegação anterior */}
            {selectedPhotoArray && selectedPhotoArray.length > 1 && selectedPhotoIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const prevIndex = selectedPhotoIndex - 1;
                  setSelectedPhotoIndex(prevIndex);
                  setSelectedPhoto(selectedPhotoArray[prevIndex]);
                }}
                className="absolute left-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 z-10 transition-colors"
                title="Foto anterior"
              >
                <ChevronLeft size={24} />
              </button>
            )}

            {/* Imagem em tamanho maior */}
            <img
              src={selectedPhoto}
              alt={`Foto ${selectedPhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Navegação próxima */}
            {selectedPhotoArray && selectedPhotoArray.length > 1 && selectedPhotoIndex < selectedPhotoArray.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const nextIndex = selectedPhotoIndex + 1;
                  setSelectedPhotoIndex(nextIndex);
                  setSelectedPhoto(selectedPhotoArray[nextIndex]);
                }}
                className="absolute right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 z-10 transition-colors"
                title="Próxima foto"
              >
                <ChevronRight size={24} />
              </button>
            )}

            {/* Contador de fotos */}
            {selectedPhotoArray && selectedPhotoArray.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-bold">
                {selectedPhotoIndex + 1} / {selectedPhotoArray.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

